import React from "react";
import { useHumanInTheLoop } from "@copilotkit/react-core/v2";
import { z } from "zod";
import { AskUserConfirmation, AskUserConfirmationArgs } from "./ask-user/ask-user-confirmation";
import { HitlSummary } from "./hitl-summary";

const parameters = z.object({
    title: z
        .string()
        .optional()
        .describe("Optional heading shown above the message."),
    message: z.string().describe("The text the user is asked to confirm."),
    confirmLabel: z
        .string()
        .optional()
        .describe("Label for the confirm button (default 'Confirm')."),
    cancelLabel: z
        .string()
        .optional()
        .describe("Label for the cancel button (default 'Cancel').")
});

/**
 * Confirmation human-in-the-loop tool. The agent calls `confirmUser` when it only needs a yes/no
 * decision — most importantly before any destructive action (deleting an app or a component). It
 * renders a message with Confirm / Cancel buttons rather than the arbitrary form `askUser` renders.
 * Returns a JSON string: { "confirmed": true } if the user confirms, { "confirmed": false } if they
 * cancel.
 */
export const useConfirmUserTool = () => {
    useHumanInTheLoop({
        name: "confirmUser",
        description:
            "Ask the user to confirm or cancel an action. Use this when you only need a yes/no decision — " +
            "especially before a destructive action such as deleting an application or dropping a component — " +
            "rather than collecting field input (for that use askUser). Returns a JSON string: " +
            '{ "confirmed": true } if the user confirms, { "confirmed": false } if they cancel.',
        parameters,
        render: ({
            args,
            respond,
            status,
            name,
            result
        }: {
            args: AskUserConfirmationArgs;
            respond: (r: string) => void;
            status: string;
            name: string;
            result?: string;
        }) => {
            if (status === "complete") return <HitlSummary toolName={name} result={result ?? ""} />;
            if (status !== "executing") return <></>;
            return <AskUserConfirmation args={args} respond={respond} />;
        }
    });
};
